/**
 * Client-Side Cache Utility
 * 
 * Implements in-memory caching with TTL (Time To Live) and LRU (Least Recently Used) eviction.
 * Designed for caching API responses to reduce server load and improve UX.
 * 
 * @example
 * const cache = Cache.getInstance();
 * 
 * // Store data
 * cache.set('categories', categoriesData, 5 * 60 * 1000); // 5 min TTL
 * 
 * // Retrieve data
 * const categories = cache.get('categories');
 */

interface CacheEntry<T> {
    data: T;
    expiresAt: number;
    lastAccessed: number;
}

export class Cache {
    private static instance: Cache;
    private cache: Map<string, CacheEntry<any>> = new Map();
    private readonly maxSize: number = 100; // Max number of entries

    private constructor() {
        // Clean up expired entries every minute
        setInterval(() => this.cleanExpired(), 60 * 1000);
    }

    public static getInstance(): Cache {
        if (!Cache.instance) {
            Cache.instance = new Cache();
        }
        return Cache.instance;
    }

    /**
     * Store data in cache with TTL
     * @param key Cache key
     * @param data Data to cache
     * @param ttl Time to live in milliseconds (default: 5 minutes)
     */
    public set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
        // Evict oldest entry if cache is full
        if (this.cache.size >= this.maxSize) {
            this.evictLRU();
        }

        this.cache.set(key, {
            data,
            expiresAt: Date.now() + ttl,
            lastAccessed: Date.now()
        });
    }

    /**
     * Retrieve data from cache
     * @param key Cache key
     * @returns Cached data or null if not found/expired
     */
    public get<T>(key: string): T | null {
        const entry = this.cache.get(key);

        if (!entry) {
            return null;
        }

        // Check if expired
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return null;
        }

        // Update last accessed time for LRU
        entry.lastAccessed = Date.now();
        return entry.data as T;
    }

    /**
     * Check if key exists and is valid
     */
    public has(key: string): boolean {
        return this.get(key) !== null;
    }

    /**
     * Invalidate a specific cache entry
     */
    public invalidate(key: string): void {
        this.cache.delete(key);
    }

    /**
     * Invalidate all cache entries matching a pattern
     * @param pattern RegExp pattern to match keys
     */
    public invalidatePattern(pattern: RegExp): void {
        const keysToDelete: string[] = [];

        for (const key of this.cache.keys()) {
            if (pattern.test(key)) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key));
    }

    /**
     * Clear all cache entries
     */
    public clear(): void {
        this.cache.clear();
    }

    /**
     * Remove expired entries
     */
    private cleanExpired(): void {
        const now = Date.now();
        const keysToDelete: string[] = [];

        for (const [key, entry] of this.cache.entries()) {
            if (now > entry.expiresAt) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key));
    }

    /**
     * Evict least recently used entry
     */
    private evictLRU(): void {
        let oldestKey: string | null = null;
        let oldestTime: number = Infinity;

        for (const [key, entry] of this.cache.entries()) {
            if (entry.lastAccessed < oldestTime) {
                oldestTime = entry.lastAccessed;
                oldestKey = key;
            }
        }

        if (oldestKey) {
            this.cache.delete(oldestKey);
        }
    }

    /**
     * Get cache statistics for debugging
     */
    public getStats(): {
        size: number;
        maxSize: number;
        entries: string[];
    } {
        return {
            size: this.cache.size,
            maxSize: this.maxSize,
            entries: Array.from(this.cache.keys())
        };
    }
}

export default Cache.getInstance();
